import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons/faEllipsis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Divider,
  LoadingOverlay,
  Menu,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useRouter } from "next/navigation";
import { type PropsWithChildren, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  useArchiveScreenerMutation,
  useCopyScreenerMutation,
} from "@/hooks/queries/screener-builder.queries";
import {
  useGetScreenerBuilderApiErrorHandler,
  useScreenerBuilderEnabled,
  useScreenerParamsWithNewCheck,
} from "@/hooks/screener-builder-hooks";
import { GenerationStatus } from "@/types/screener-builder";
import { FEATURE_FLAGS, useFeatureIsOn } from "@/utils/growthbook";
import { buildTestId } from "@/utils/helpers";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

export const LEGACY = "legacy";
export type HeaderMenuGenerationStatus = GenerationStatus | "legacy";

interface DefaultHeaderMenuProps {
  copyDisabledMessage?: string;
  disabled?: boolean;
  generationStatus: HeaderMenuGenerationStatus;
  loading?: boolean;
}

export const DefaultHeaderMenu = ({
  children,
  copyDisabledMessage,
  disabled = false,
  generationStatus,
  loading = false,
}: PropsWithChildren<DefaultHeaderMenuProps>) => {
  const router = useRouter();
  const screenerBuilderEnabled = useScreenerBuilderEnabled();
  const viewRestoreArchivedScreenersEnabled = useFeatureIsOn(
    FEATURE_FLAGS.viewRestoreArchivedScreeners,
  );
  const errorHandler = useGetScreenerBuilderApiErrorHandler();
  const [screenerId, idIsNew] = useScreenerParamsWithNewCheck();
  // Tracking the loading state manually to prevent a gap between `isPending`
  // and the re-route back to home.
  const [mutationLoading, setMutationLoading] = useState(false);
  const { mutate: copyScreener } = useCopyScreenerMutation();
  const { mutate: archiveScreener } = useArchiveScreenerMutation();
  const [
    confirmArchiveModalOpened,
    { close: closeConfirmArchiveModal, open: openConfirmArchiveModal },
  ] = useDisclosure();
  const track = useTrackAnalytics({ screener_id: screenerId });

  const handleCopy = () => {
    track("Copy Interview Button Clicked");
    setMutationLoading(true);

    copyScreener(screenerId, {
      onError: (error) => {
        setMutationLoading(false);
        errorHandler(error);
      },
      onSuccess: (copyId) => {
        showUpwageNotification("Screener copied", NotificationTypes.Success);
        router.push(`/screener-builder/${copyId}`);
        setMutationLoading(false);
      },
    });
  };

  const handleArchive = () => {
    track("Archive Interview Button Clicked");
    setMutationLoading(true);

    archiveScreener(screenerId, {
      onError: (error) => {
        setMutationLoading(false);
        errorHandler(error);
      },
      onSuccess: () => {
        showUpwageNotification("Screener archived", NotificationTypes.Success);
        router.push("/");
      },
    });
  };

  const copyTooltip = useMemo(() => {
    if (copyDisabledMessage) {
      return copyDisabledMessage;
    }

    if (idIsNew) {
      return "Please save your new screener before copying.";
    }

    switch (generationStatus) {
      case GenerationStatus.Generating:
        return "Copy unavailable while screener is generating. Please try again in a few minutes.";
      case GenerationStatus.Error:
        return "There was an error generating this screener, unable to copy.";
      // Valid statuses for copy, no error message
      case GenerationStatus.Complete:
      case GenerationStatus.New:
        return undefined;
      // Copy is unavailable and completely removed from the page for legacy
      // screeners. Archive remains available — see the Menu.Dropdown below.
      case LEGACY:
      default:
        return undefined;
    }
  }, [copyDisabledMessage, generationStatus, idIsNew]);

  return (
    <>
      <LoadingOverlay visible={mutationLoading} />
      <Menu>
        <Menu.Target>
          <ActionIcon
            className={twMerge([
              "disabled:bg-transparent",
              "disabled:opacity-50",
              "disabled:text-uw-purple",
            ])}
            data-testid={buildTestId(
              "screener-builder",
              "header",
              "menu-button",
            )}
            disabled={disabled}
            loading={loading}
            variant="transparent"
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {screenerBuilderEnabled && (
            <>
              {generationStatus !== LEGACY && (
                <Menu.Item disabled={!!copyTooltip} onClick={handleCopy}>
                  <Tooltip disabled={!copyTooltip} label={copyTooltip}>
                    <div>Copy Interview</div>
                  </Tooltip>
                </Menu.Item>
              )}
              <Menu.Item
                disabled={idIsNew}
                onClick={
                  viewRestoreArchivedScreenersEnabled
                    ? handleArchive
                    : openConfirmArchiveModal
                }
              >
                <Tooltip
                  disabled={!idIsNew}
                  label="This screener has not yet been saved"
                >
                  <span>Archive Interview</span>
                </Tooltip>
              </Menu.Item>
            </>
          )}
          {children}
        </Menu.Dropdown>
      </Menu>
      <Modal
        classNames={{
          body: "pb-6 px-[30px]",
          header: "pt-6 px-[30px]",
        }}
        closeButtonProps={{
          disabled: mutationLoading,
          icon: (
            <FontAwesomeIcon
              className="bg-uw-gray-9 rounded-full text-lg text-uw-gray-0"
              icon={faCircleX}
            />
          ),
        }}
        onClose={closeConfirmArchiveModal}
        opened={confirmArchiveModalOpened}
        title={
          <Text fw={600} size="lg">
            Archive interview?
          </Text>
        }
      >
        <Text size="sm">{"Archived interviews can't be recovered."}</Text>

        <Divider className="mb-6 mt-[18px]" color="gray.2" />
        <Button
          className="mr-2"
          color="red.3"
          loading={mutationLoading}
          onClick={handleArchive}
        >
          Archive interview
        </Button>
        <Button
          c="gray.9"
          color="gray.0"
          disabled={mutationLoading}
          onClick={closeConfirmArchiveModal}
        >
          Cancel
        </Button>
      </Modal>
    </>
  );
};
